import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Select from "react-select";
import DatePicker from "react-date-picker";
import moment from "moment";
import { setMainLoading, setPageTitle } from "../../Store/reducers/Common";
import { PostAxios, responseCatchFunc } from "../../Services/RequestService";
import { setToastData } from "../../Store/reducers/Toast";
import { ConvertErrorArrayToString } from "../../Services/Common";

const AddTakhmeen = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { userData, accessToken } = useSelector((s) => s.user);
    const [orgList, setOrgList] = useState([]);
    const [divList, setDivList] = useState([]);
    const [divisionMembers, setDivisionMembers] = useState([]);
    const [filter, setFilter] = useState({ org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id, div_id: 0 });

    const memberOptions = useMemo(() => divisionMembers.map((m) => ({ value: m.user_id, label: `${m.full_name.trim()} (${m.user_its})` })), [divisionMembers]);
    const divOptions = useMemo(() => divList.filter((d) => parseInt(d.org_id, 10) === parseInt(filter.org_id, 10)).map((d) => ({ value: d.ID, label: `${d.division_name} (${d.division_number})` })), [divList, filter.org_id]);

    const loadDivisionMembers = (orgId, divId) => {
        if (!orgId || !divId) { setDivisionMembers([]); return; }
        PostAxios("takhmeen/division-members", { org_id: orgId, div_id: divId }, accessToken)
            .then(({ data }) => { if (data.status === "1") setDivisionMembers(data.list || []); });
    };

    useEffect(() => {
        dispatch(setPageTitle("Add Takhmeen"));
        const promises = [];
        if (userData.role_slug === "super_admin") promises.push(PostAxios("masters/organization", { page: 1, perPage: 1000 }, accessToken));
        promises.push(PostAxios("masters/division-list", { org_id: userData.role_slug === "super_admin" ? 0 : userData.org_id, page: 1, perPage: 1000 }, accessToken));
        dispatch(setMainLoading(true));
        Promise.all(promises).then((res) => {
            res.forEach((r) => {
                if (r.config.url.includes("organization") && r.data.status === "1") setOrgList(r.data.data);
                if (r.config.url.includes("division-list") && r.data.status === "1") setDivList(r.data.data);
            });
        }).finally(() => dispatch(setMainLoading(false)));
    }, []);

    return (
        <div className="main-form-sec">
            <Formik
                initialValues={{
                    org_id: parseInt(userData.org_id, 10) || 0,
                    div_id: null,
                    takhmeen_name: "",
                    description: "",
                    start_date: "",
                    end_date: "",
                    base_amount: "",
                    finance_incharges: [],
                    project_incharges: [],
                    coordinators: [],
                }}
                validate={(v) => {
                    const e = {};
                    if (parseInt(v.org_id, 10) < 1) e.org_id = "Organization required";
                    if (!v.div_id) e.div_id = "Division required";
                    if (!v.takhmeen_name.trim()) e.takhmeen_name = "Name required";
                    if (!v.start_date) e.start_date = "Start date required";
                    if (!v.end_date) e.end_date = "End date required";
                    if (!v.base_amount || parseFloat(v.base_amount) <= 0) e.base_amount = "Amount required";
                    if (!v.finance_incharges.length) e.finance_incharges = "At least one Finance Incharge required";
                    if (!v.project_incharges.length) e.project_incharges = "At least one Project Incharge required";
                    if (!v.coordinators.length) e.coordinators = "At least one coordinator required";
                    return e;
                }}
                onSubmit={(values, { setSubmitting }) => {
                    dispatch(setMainLoading(true));
                    PostAxios("takhmeen/add-takhmeen", {
                        org_id: values.org_id,
                        div_id: values.div_id.value,
                        takhmeen_name: values.takhmeen_name,
                        description: values.description,
                        start_date: moment(values.start_date).format("YYYY-MM-DD"),
                        end_date: moment(values.end_date).format("YYYY-MM-DD"),
                        base_amount: values.base_amount,
                        finance_incharge_ids: JSON.stringify(values.finance_incharges.map((c) => c.value)),
                        project_incharge_ids: JSON.stringify(values.project_incharges.map((c) => c.value)),
                        coordinator_ids: JSON.stringify(values.coordinators.map((c) => c.value)),
                    }, accessToken).then(({ data }) => {
                        if (data.status === "1") {
                            dispatch(setToastData({ message: data.message, type: "success", showCloseBtn: true }));
                            navigate("/takhmeen/view", { state: { takhmeen_id: data.data.takhmeen_id } });
                        } else {
                            dispatch(setToastData({ message: data.errors ? ConvertErrorArrayToString(data.errors) : data.message, type: "error", showCloseBtn: true }));
                        }
                    }).catch((e) => responseCatchFunc(dispatch, e)).finally(() => { setSubmitting(false); dispatch(setMainLoading(false)); });
                }}
            >
                {({ values, setFieldValue, errors, touched, isSubmitting }) => (
                    <Form>
                        <div className="form-sec">
                            {userData.role_slug === "super_admin" && (
                                <div className={`form-field-group w-50 ${errors.org_id && touched.org_id ? "error-input" : ""}`}>
                                    <div className="w-50"><label>Organization*</label></div>
                                    <div className="w-50">
                                        <Field as="select" name="org_id" className="select-input" onChange={(e) => {
                                            setFilter({ org_id: e.target.value, div_id: 0 });
                                            setFieldValue("org_id", e.target.value);
                                            setFieldValue("div_id", null);
                                            setFieldValue("finance_incharges", []);
                                            setFieldValue("project_incharges", []);
                                            setFieldValue("coordinators", []);
                                        }}>
                                            <option value={0}>Choose</option>
                                            {orgList.map((o) => <option key={o.ID} value={o.ID}>{o.short_name}</option>)}
                                        </Field>
                                        <ErrorMessage name="org_id" component="span" className="error-label" />
                                    </div>
                                </div>
                            )}
                            <div className={`form-field-group w-50 ${errors.div_id && touched.div_id ? "error-input" : ""}`}>
                                <div className="w-50"><label>Takhmeen for Division*</label></div>
                                <div className="w-50 input-area text-left">
                                    <Select options={divOptions} value={values.div_id} placeholder="Choose division"
                                        onChange={(opt) => {
                                            setFieldValue("div_id", opt);
                                            setFilter({ ...filter, div_id: opt?.value || 0 });
                                            loadDivisionMembers(values.org_id, opt?.value);
                                            setFieldValue("finance_incharges", []);
                                            setFieldValue("project_incharges", []);
                                            setFieldValue("coordinators", []);
                                        }} />
                                    <ErrorMessage name="div_id" component="span" className="error-label" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.takhmeen_name && touched.takhmeen_name ? "error-input" : ""}`}>
                                <div className="w-50"><label>Takhmeen Name*</label></div>
                                <div className="w-50"><Field name="takhmeen_name" className="form-input" /><ErrorMessage name="takhmeen_name" component="span" className="error-label" /></div>
                            </div>
                            <div className="form-field-group w-50">
                                <div className="w-50"><label>Description</label></div>
                                <div className="w-50"><Field as="textarea" name="description" className="form-input" rows={3} /></div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.base_amount && touched.base_amount ? "error-input" : ""}`}>
                                <div className="w-50"><label>Takhmeen Amount*</label></div>
                                <div className="w-50"><Field name="base_amount" type="number" min="1" step="1" className="form-input"  onWheel={(e) => e.currentTarget.blur()} /><ErrorMessage name="base_amount" component="span" className="error-label" /></div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.start_date && touched.start_date ? "error-input" : ""}`}>
                                <div className="w-50"><label>Start Date*</label></div>
                                <div className="w-50">
                                    <div className="date-picker-sec">
                                        <DatePicker
                                            value={values.start_date}
                                            onChange={(d) => setFieldValue("start_date", d)}
                                            clearIcon={null}
                                            format="dd-MM-y"
                                            dayPlaceholder="DD"
                                            monthPlaceholder="MM"
                                            yearPlaceholder="YYYY"
                                            className="form-input" />
                                    </div>
                                    <ErrorMessage name="start_date" component="span" className="error-label" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.end_date && touched.end_date ? "error-input" : ""}`}>
                                <div className="w-50"><label>End Date*</label></div>
                                <div className="w-50">
                                    <div className="date-picker-sec">
                                        <DatePicker
                                            value={values.end_date}
                                            onChange={(d) => setFieldValue("end_date", d)}
                                            clearIcon={null}
                                            format="dd-MM-y"
                                            dayPlaceholder="DD"
                                            monthPlaceholder="MM"
                                            yearPlaceholder="YYYY"
                                            className="form-input" /></div>
                                    <ErrorMessage name="end_date" component="span" className="error-label" /></div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.finance_incharges && touched.finance_incharges ? "error-input" : ""}`}>
                                <div className="w-50"><label>Finance Incharge*</label></div>
                                <div className="w-50 input-area text-left">
                                    <Select isMulti options={memberOptions} value={values.finance_incharges} onChange={(v) => setFieldValue("finance_incharges", v || [])} placeholder="Select finance incharge(s)" isDisabled={!values.div_id} />
                                    <ErrorMessage name="finance_incharges" component="span" className="error-label" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.project_incharges && touched.project_incharges ? "error-input" : ""}`}>
                                <div className="w-50"><label>Project Incharge*</label></div>
                                <div className="w-50 input-area text-left">
                                    <Select isMulti options={memberOptions} value={values.project_incharges} onChange={(v) => setFieldValue("project_incharges", v || [])} placeholder="Select project incharge(s)" isDisabled={!values.div_id} />
                                    <ErrorMessage name="project_incharges" component="span" className="error-label" />
                                </div>
                            </div>
                            <div className={`form-field-group w-50 ${errors.coordinators && touched.coordinators ? "error-input" : ""}`}>
                                <div className="w-50"><label>Coordinators*</label></div>
                                <div className="w-50 input-area text-left">
                                    <Select isMulti options={memberOptions} value={values.coordinators} onChange={(v) => setFieldValue("coordinators", v || [])} placeholder="Select coordinators" isDisabled={!values.div_id} />
                                    <ErrorMessage name="coordinators" component="span" className="error-label" />
                                </div>
                            </div>
                        </div>
                        <div className="w-100 d-flex justify-content-start align-items-center">
                            <button type="submit" className="btn btn-blue" disabled={isSubmitting}>Create Takhmeen</button>
                            <button type="button" className="btn btn-blue ms-2" onClick={() => navigate("/takhmeen")}>Cancel</button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default AddTakhmeen;
